import { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { DateRangePickerProps } from '@cloudscape-design/components';

import { getApiClient, publicClient } from '../../api/client';
import { buildListQueryParams } from './query';

export async function getDripEmailReport(uuid: string) {
  // Public endpoint - no auth needed
  const { data, error } = await publicClient.GET('/drip/emails/{uuid}/report', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to fetch drip email report: ${JSON.stringify(error)}`);
  return data;
}

// --- Admin/Dashboard functions ---

export type DripScopeParams = {
  campaign_uuid?: string | null;
  zip_blast_uuid?: string | null;
};

export async function getDripEmails(
  query: PropertyFilterQuery,
  page = 1,
  limit = 20,
  range?: DateRangePickerProps.Value | null,
  sortingField?: string,
  sortDirection?: 'asc' | 'desc',
  now = new Date(),
  textQuery?: string,
  scope?: DripScopeParams,
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query, page, limit, range, now,
    rangeName: 'scheduled_at',
    sortingField: sortingField ?? 'scheduled_at',
    sortDirection: sortDirection ?? 'desc',
    textQuery,
  });
  const { data, error } = await client.GET('/drip/emails', {
    params: {
      query: {
        ...params,
        campaign_uuid: scope?.campaign_uuid ?? undefined,
        zip_blast_uuid: scope?.zip_blast_uuid ?? undefined,
      },
    },
  });
  if (error) throw new Error(`Failed to fetch drip emails: ${JSON.stringify(error)}`);
  return data;
}

export async function getDripEmailMeta() {
  const client = getApiClient();
  const { data, error } = await client.GET('/drip/emails/meta');
  if (error) throw new Error(`Failed to fetch drip emails meta: ${JSON.stringify(error)}`);
  return data;
}

export async function getDripEmail(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/drip/emails/{uuid}', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to fetch drip email: ${JSON.stringify(error)}`);
  return data;
}

export async function sendDripEmail(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.POST('/drip/emails/{uuid}/send', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to send drip email: ${JSON.stringify(error)}`);
  return data;
}

export async function sendDripEmails(emails: string[]) {
  for (const email of emails) await sendDripEmail(email);
}

export async function getDripDashboard(days = 30, scope?: DripScopeParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/drip/dashboard', {
    params: {
      query: {
        days,
        campaign_uuid: scope?.campaign_uuid ?? undefined,
        zip_blast_uuid: scope?.zip_blast_uuid ?? undefined,
      },
    },
  });
  if (error) throw new Error(`Failed to fetch drip dashboard: ${JSON.stringify(error)}`);
  return data;
}

export async function getDripEmailPreview(uuid: string) {
  // Read-only render of the scheduled email body: never sends or mutates status.
  const client = getApiClient();
  const { data, error } = await client.GET('/drip/emails/{uuid}/preview', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to fetch drip email preview: ${JSON.stringify(error)}`);
  return data;
}
